import type { InjectionKey } from 'vue'

export interface SocialProvider {
  loginUrl: string
  alias: string
  providerId: string
  displayName: string
}

export enum FrontendTemplate {
  LOGIN = 'login',
  ERROR = 'error',
  INFO = 'info',
  LOGIN_VERIFY_EMAIL = 'login_verify_email',
  LOGIN_UPDATE_PASSWORD = 'login_update_password',
  LOGIN_RESET_PASSWORD = 'login_reset_password',
  LOGIN_PAGE_EXPIRED = 'login_page_expired',
  LOGIN_UPDATE_PROFILE = 'login_update_profile',
  LOGIN_IDP_LINK_EMAIL = 'login_idp_link_email',
  LOGIN_IDP_LINK_CONFIRM = 'login_idp_link_confirm',
  SAML_POST_FORM = 'saml_post_form',
  REGISTER = 'register',
}

export interface CustomElementProps {
  /**
   * The frontend template to use.
   */
  template?: FrontendTemplate
  /**
   * The realm ID.
   */
  realm: string
  /**
   * URL (served by Keycloak server) where the login page is. The frontend should render this in a link. Often used in a back button.
   */
  loginUrl: string
  /**
   * The form action URL for login actions such as verify email, update & reset password, account linking, SAML profile update, etc.
   * This should be the form's action url with method POST.
   */
  loginActionUrl?: string
  /**
   * Resource path of the theme where to find images, CSS and JS. The assets folder in the filesystem.
   */
  resourcesPath: string
  /**
   * URL (served by Keycloak server) where the registration page is. The frontend should render this in a link.
   */
  registrationUrl: string
  /**
   * URL (served by Keycloak server) where the password reset page is. The frontend should render this in a link.
   */
  resetPasswordUrl: string
  /**
   * Whether to show the checkbox with remember-me functinality (handled by Keycloak server)
   */
  rememberMeEnabled: boolean
  /**
   * Whether to show the link to reset the password
   */
  resetPasswordEnabled: boolean
  /**
   * Whether to show the register button or not
   */
  registrationEnabled: boolean
  /**
   * Base url of the network, e.g. staging.starmind.com
   */
  baseUrl: string
  /**
   * Language that the Keycloak server is using for the current user
   */
  locale?: string
  /**
   * Supported locales by the Keycloak server
   */
  supportedLocales?: {
    label: string
    url: string
  }[]
  /**
   * For pages containing forms (login, registration, reset PW, etc.) to which url to submit the POST form to.
   * This should be put into a form's 'action' attribute.
   */
  formActionUrl?: string
  recaptchaSiteKey?: string
  /**
   * The currently logged in user's username (=email address) in how we use Keycloak. Used on post-login actions like update profile.
   */
  username?: string
  /**
   * The currently logged in user's email address. Used on post-login actions like update profile.
   */
  email?: string
  /**
   * The currently logged in user's first name. Used on post-login actions like update profile.
   */
  firstname?: string
  /**
   * The currently logged in user's last name. Used on post-login actions like update profile.
   */
  lastname?: string
  /**
   * When an error page about the expired login flow is rendered, this url points to where the flow can be restarted
   */
  loginRestartFlowUrl?: string
  /**
   * The error message coming from keycloak server
   */
  errorMessage?: string
  /**
   * For each error there is an action url where the button should point to
   */
  errorActionUrl?: string
  /**
   * Details for what should be rendered in the info view
   */
  infoContent?: {
    message: string
    buttonLabel?: string
    buttonHref?: string
  }
  /**
   * The username used in the last login attempt. Used for pre-filling the username field.
   */
  loginUsername?: string
  /**
   * A generic message that is rendered to the page by the Keycloak server.
   */
  genericMessage?: string
  /**
   * The type of the generic message from the Keycloak server. Typically it is displayed in a box and the type determines its color.
   */
  genericMessageType?: 'info' | 'success' | 'warning' | 'error'
  /**
   * List of enabled SSO providers to log in with (SAML, Google, etc.)
   */
  supportedSocialProviders?: SocialProvider[]
}

export const customElementPropsKey =
  Symbol() as InjectionKey<CustomElementProps>
